import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Sparkles, Trophy, Flame, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import EnthusiasmPathProgressBar from './EnthusiasmPathProgressBar';
import StudentCharacterImg from './StudentCharacterImg';
import {
  RankWithMedal,
  profileName,
  useIsCurrentSpotlightUser,
} from './DepartmentSpotlight';
import {
  getEffectiveDailyMaxPoints,
  ENTHUSIASM_DAILY_GOAL_POINTS,
} from '../../utils/enthusiasmDaily';
import { PROJECT_CHARACTERS } from '../../constants/projectCharacters';

function pickSeasonMotivKey(total) {
  const n = Number(total);
  if (!Number.isFinite(n) || n <= 0) return 'seasonPointsBarMotiv0';
  if (n < 250) return 'seasonPointsBarMotiv1';
  if (n < 900) return 'seasonPointsBarMotiv2';
  if (n < 2200) return 'seasonPointsBarMotiv3';
  return 'seasonPointsBarMotiv4';
}

function dailyMotivationTierKey(points, goal) {
  const p = Number(points) || 0;
  const g = Number(goal) || ENTHUSIASM_DAILY_GOAL_POINTS;
  if (p >= g) return 'complete';
  if (p >= 24) return 'tier3';
  if (p >= 12) return 'tier2';
  if (p >= 1) return 'tier1';
  return 'tier0';
}

function DailyPathProgressSkeleton() {
  return (
    <div className="w-full animate-pulse" aria-hidden>
      <div className="mb-1 flex justify-between gap-2">
        <div className="h-2.5 w-32 rounded bg-slate-200/90" />
        <div className="h-4 w-10 rounded bg-slate-200/90" />
      </div>
      <div className="h-2.5 w-full rounded-full bg-indigo-100/90 ring-1 ring-inset ring-indigo-200/50" />
      <div className="mt-1.5 flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-1.5 flex-1 rounded-full bg-slate-200/70" />
        ))}
      </div>
    </div>
  );
}

/**
 * بطاقة أعلى الصفحة الرئيسية للطالب — ترحيب، موسم، نقاط اليوم، مسار اليوم، ترتيب القسم.
 */
function StudentSeasonEnthusiasmBar({ dailyData, dailyLoading, anchorId = 'student-enthusiasm-panel' }) {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const { user } = useAuth();
  const isMe = useIsCurrentSpotlightUser();

  const [seasonLoading, setSeasonLoading] = useState(true);
  const [seasonData, setSeasonData] = useState(null);

  const [spotlightLoading, setSpotlightLoading] = useState(true);
  const [spotlightData, setSpotlightData] = useState(null);
  const [spotlightError, setSpotlightError] = useState(null);

  const firstName = user?.firstName?.trim();
  const gender = user?.gender;
  const isFemale = gender === 'female';
  const character = isFemale ? PROJECT_CHARACTERS.lanaWelcome : PROJECT_CHARACTERS.zaydWelcome;

  const locale = currentLanguage === 'ar' ? 'ar' : currentLanguage === 'en' ? 'en' : 'fr';
  const formatTotal = useMemo(() => {
    const fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    return (v) => fmt.format(Number(v) || 0);
  }, [locale]);

  const dailyTotalNum =
    dailyData?.total != null && !Number.isNaN(Number(dailyData.total)) ? Number(dailyData.total) : 0;

  const dailyMotivText = useMemo(() => {
    if (dailyLoading) return t('dashboard.student.enthusiasmMotivTier0');
    const g = dailyData ? getEffectiveDailyMaxPoints(dailyData) : ENTHUSIASM_DAILY_GOAL_POINTS;
    const tier = dailyMotivationTierKey(dailyTotalNum, g);
    const map = {
      tier0: 'dashboard.student.enthusiasmMotivTier0',
      tier1: 'dashboard.student.enthusiasmMotivTier1',
      tier2: 'dashboard.student.enthusiasmMotivTier2',
      tier3: 'dashboard.student.enthusiasmMotivTier3',
      complete: 'dashboard.student.enthusiasmMotivTierComplete',
    };
    return t(map[tier] || map.tier0);
  }, [dailyData, dailyLoading, dailyTotalNum, t]);

  const encouragementOneLine = useMemo(() => {
    const parts = [
      t('dashboard.student.welcomeBoxEncourage'),
      dailyMotivText,
      t('dashboard.student.homeWelcomeSubtitle'),
    ];
    return parts.join(' · ');
  }, [t, dailyMotivText]);

  const caption = useMemo(() => {
    const g = dailyData ? getEffectiveDailyMaxPoints(dailyData) : ENTHUSIASM_DAILY_GOAL_POINTS;
    const total = dailyData?.total != null ? Number(dailyData.total) : 0;
    const cur = Number.isFinite(total) && total >= 0 ? total : 0;
    return t('dashboard.student.enthusiasmProgressCaptionWithMax', {
      current: Math.round(cur),
      max: Math.round(g),
    });
  }, [dailyData, t]);

  useEffect(() => {
    if (!user?.id) {
      setSeasonLoading(false);
      setSeasonData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setSeasonLoading(true);
      try {
        const res = await api.get(`/students/user/${user.id}/enthusiasm/season`);
        if (!cancelled && res.data?.success && res.data.data) {
          setSeasonData(res.data.data);
        } else if (!cancelled) {
          setSeasonData(null);
        }
      } catch {
        if (!cancelled) setSeasonData(null);
      } finally {
        if (!cancelled) setSeasonLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSpotlightLoading(true);
      setSpotlightError(null);
      try {
        const res = await api.get('/students/enthusiasm/department-spotlight');
        if (!cancelled && res.data?.success) {
          setSpotlightData(res.data.data);
        } else if (!cancelled) {
          setSpotlightError('bad');
        }
      } catch {
        if (!cancelled) setSpotlightError('network');
      } finally {
        if (!cancelled) setSpotlightLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const l1 = spotlightData?.leaders?.[0];
  const l2 = spotlightData?.leaders?.[1];
  const ci = spotlightData?.closingIn;

  const labelFor = useCallback(
    (entry) => {
      if (!entry) return '—';
      if (isMe(entry)) return t('dashboard.student.spotlightYou');
      return profileName(entry.profile);
    },
    [isMe, t]
  );

  const n1 = l1 ? labelFor(l1) : '';
  const n2 = l2 ? labelFor(l2) : '';
  const nc = ci
    ? isMe({ userId: ci.userId, profile: ci.profile })
      ? t('dashboard.student.spotlightYou')
      : profileName(ci.profile)
    : '';

  const warnText =
    n1 && n2 && nc
      ? t('dashboard.student.spotlightWarn', { first: n1, second: n2, chaser: nc })
      : '';

  if (!user?.id) return null;

  const showSeasonRow = Boolean(!seasonLoading && seasonData);
  const hasSeasonShell = seasonLoading || showSeasonRow;
  const totalNum =
    seasonData?.total != null && !Number.isNaN(Number(seasonData.total)) ? Number(seasonData.total) : 0;
  const active = Boolean(seasonData?.activeSeason);
  const seasonYear =
    typeof seasonData?.seasonYear === 'string' && seasonData.seasonYear.trim()
      ? seasonData.seasonYear.trim()
      : null;
  const motivKey = pickSeasonMotivKey(totalNum);

  const levelNum =
    !dailyLoading &&
    dailyData?.studentLevelNumber != null &&
    Number(dailyData.studentLevelNumber) > 0
      ? Number(dailyData.studentLevelNumber)
      : null;

  const shellCls = hasSeasonShell
    ? 'border-white/60 bg-gradient-to-br from-sky-500/[0.14] via-fuchsia-500/[0.08] to-amber-400/[0.16] shadow-[0_16px_40px_-22px_rgba(59,130,246,0.45)]'
    : 'border-slate-200/80 bg-gradient-to-r from-slate-50/90 via-white to-sky-50/25 shadow-[0_12px_32px_-22px_rgba(100,116,139,0.2)]';

  const showSpotlightRanks =
    !spotlightLoading && !spotlightError && spotlightData && n1 && n2;

  const seasonTotalButton =
    seasonLoading ? (
      <span
        className="h-12 w-full shrink-0 animate-pulse rounded-2xl border-2 border-amber-200/60 bg-white/50 sm:h-[5.75rem] sm:w-24"
        aria-hidden
      />
    ) : showSeasonRow ? (
      <button
        type="button"
        className={`group relative flex min-h-[3.25rem] w-full shrink-0 flex-row items-center justify-between gap-3 overflow-hidden rounded-2xl border-2 px-4 py-2.5 text-center shadow-[0_4px_0_0_rgba(15,23,42,0.12)] transition hover:brightness-[1.04] active:translate-y-0.5 active:shadow-[0_2px_0_0_rgba(15,23,42,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 sm:h-[5.75rem] sm:min-h-0 sm:w-[6.25rem] sm:flex-col sm:justify-center sm:gap-0 sm:px-2.5 sm:py-2.5 ${
          active
            ? 'border-amber-400/90 bg-gradient-to-br from-amber-400 via-orange-500 to-rose-600 text-white shadow-[0_4px_0_0_rgba(180,83,9,0.45)] hover:shadow-[0_5px_0_0_rgba(180,83,9,0.35)] active:shadow-[0_2px_0_0_rgba(180,83,9,0.4)]'
            : 'border-slate-300/90 bg-gradient-to-br from-slate-100 to-slate-200/95 text-slate-900 ring-1 ring-white/60 shadow-[0_4px_0_0_rgba(100,116,139,0.25)]'
        }`}
        aria-label={`${t('dashboard.student.seasonPointsBarTotalLabel')}: ${formatTotal(totalNum)}`}
      >
        <span
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl"
          aria-hidden
        >
          <span
            className={`pointer-events-none absolute inset-y-0 left-0 w-[46%] bg-gradient-to-r from-transparent to-transparent motion-reduce:animate-none animate-season-total-shimmer sm:w-[42%] ${
              active ? 'via-white/40' : 'via-white/80'
            }`}
          />
        </span>
        <span
          className={`relative z-[1] min-w-0 max-w-[55%] text-start text-[10px] font-bold uppercase leading-tight tracking-wide sm:max-w-none sm:text-center sm:text-[9px] ${
            active ? 'text-white/90' : 'text-slate-600'
          }`}
        >
          {t('dashboard.student.seasonPointsBarTotalLabel')}
        </span>
        <span
          className={`relative z-[1] shrink-0 font-black tabular-nums leading-none sm:mt-1 ${
            active ? 'text-2xl text-white drop-shadow-sm sm:text-xl' : 'text-2xl text-slate-900 sm:text-xl'
          }`}
        >
          {formatTotal(totalNum)}
        </span>
      </button>
    ) : null;

  return (
    <section
      id={anchorId}
      className={`relative w-full min-w-0 scroll-mt-20 overflow-hidden rounded-[1.35rem] border p-4 sm:rounded-[1.5rem] sm:p-5 ${shellCls}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      aria-label={`${t('dashboard.student.homeWelcomeTitle')} — ${t('dashboard.student.seasonPointsBarAria')}`}
    >
      <div
        className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full bg-cyan-300/25 blur-2xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-12 -left-8 h-36 w-44 rounded-full bg-violet-400/15 blur-2xl"
        aria-hidden
      />

      {/* ترحيب — موبايل: صف كامل ثم زر الموسم بعرض كامل؛ sm+ مع dir على القسم: المحتوى عند main-start ثم الزر (يمين/يسار RTL صحيح) */}
      <div className="relative mb-4 flex w-full flex-col items-stretch gap-3 sm:mb-5 sm:flex-row sm:items-start sm:gap-4">
        <div className="order-1 flex min-w-0 w-full flex-1 flex-row items-start gap-3 sm:order-1 sm:gap-4">
          <div className="relative flex h-[5.25rem] w-20 shrink-0 items-end justify-center sm:h-[5.75rem] sm:w-[5.25rem]">
            <div
              className="absolute inset-x-0 bottom-0 h-[55%] rounded-full bg-gradient-to-t from-sky-200/45 to-transparent blur-md"
              aria-hidden
            />
            <StudentCharacterImg
              imageFile={character.imageFile}
              alt={t(character.i18nAltKey)}
              className={`relative z-[1] max-h-full max-w-full object-contain drop-shadow-md ${
                isRTL ? 'object-top-right' : 'object-top-left'
              }`}
            />
          </div>
          <div className="min-w-0 flex-1 pt-0.5 text-start">
          {/* سطر واحد: Ton parcours… — Saison année — Niveau (مخفي على الموبايل) */}
          <div className="mb-0 hidden min-w-0 flex-nowrap items-center gap-x-1.5 overflow-x-auto pb-0.5 text-[10px] font-bold text-slate-800 sm:mb-2.5 sm:flex sm:gap-x-2 sm:text-[11px]">
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 font-semibold tracking-tight text-sky-950 ring-1 ring-sky-400/30 sm:px-2.5 sm:py-0.5">
              <Sparkles className="h-3 w-3 shrink-0 text-sky-600 sm:h-3.5 sm:w-3.5" strokeWidth={2} aria-hidden />
              <span className="whitespace-nowrap">{t('dashboard.student.homeWelcomeTitle')}</span>
            </span>
            {seasonLoading ? (
              <span className="inline-flex h-4 min-w-[6rem] shrink-0 animate-pulse rounded-md bg-white/60" aria-hidden />
            ) : showSeasonRow ? (
              <>
                <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-sky-400/35 bg-sky-500/10 px-2 py-0.5 font-black uppercase tracking-[0.06em] text-sky-900 ring-1 ring-sky-300/40">
                  <Trophy className="h-3 w-3 text-amber-600" strokeWidth={2.5} aria-hidden />
                  {t('dashboard.student.seasonPointsBarBadge')}
                  {seasonYear ? (
                    <span className="ps-0.5 font-bold normal-case tracking-normal text-slate-900">
                      {' '}
                      {seasonYear}
                    </span>
                  ) : null}
                </span>
                {!active ? (
                  <span className="inline-flex shrink-0 rounded-full bg-amber-100/90 px-1.5 py-px text-[9px] font-bold text-amber-900 ring-1 ring-amber-200/80 sm:text-[10px]">
                    {t('dashboard.student.seasonPointsBarInactiveBadge')}
                  </span>
                ) : null}
                {dailyLoading ? (
                  <span className="inline-flex h-4 w-14 shrink-0 animate-pulse rounded-md bg-white/55" aria-hidden />
                ) : levelNum != null ? (
                  <span className="inline-flex shrink-0 whitespace-nowrap rounded-full border border-indigo-200/90 bg-indigo-50/95 px-2 py-0.5 font-black uppercase tracking-wide text-indigo-900/90 ring-1 ring-indigo-100/80">
                    {t('dashboard.student.enthusiasmProgressLevel', { level: levelNum })}
                  </span>
                ) : null}
              </>
            ) : !dailyLoading && levelNum != null ? (
              <span className="inline-flex shrink-0 whitespace-nowrap rounded-full border border-indigo-200/90 bg-indigo-50/95 px-2 py-0.5 font-black uppercase tracking-wide text-indigo-900/90 ring-1 ring-indigo-100/80">
                {t('dashboard.student.enthusiasmProgressLevel', { level: levelNum })}
              </span>
            ) : null}
          </div>
          {firstName ? (
            <h1 className="mt-1 text-xl font-black tracking-tight text-slate-900 sm:text-2xl">
              <span className="bg-gradient-to-r from-sky-700 to-violet-700 bg-clip-text text-transparent">
                {t('dashboard.student.homeGreetingNamed', { name: firstName })}
              </span>
            </h1>
          ) : (
            <h1 className="mt-1 text-lg font-black tracking-tight text-slate-900 sm:text-xl">
              {t('dashboard.student.homeWelcomeTitle')}
            </h1>
          )}

          {/* تشجيع الموسم — مباشرة تحت التحية بالاسم */}
          {seasonLoading ? (
            <div className="mt-2 h-10 w-full max-w-xl animate-pulse rounded-lg bg-white/45" aria-hidden />
          ) : showSeasonRow ? (
            <p className="mt-2 text-sm font-semibold leading-snug text-slate-800 sm:mt-2.5 sm:text-[0.9375rem]">
              {active ? t(`dashboard.student.${motivKey}`) : t('dashboard.student.seasonPointsBarOffSeason')}
            </p>
          ) : null}
          </div>
        </div>
        {seasonTotalButton ? (
          <div className="order-2 w-full shrink-0 sm:order-2 sm:w-auto">
            {seasonTotalButton}
          </div>
        ) : null}
      </div>

      {!seasonLoading && !showSeasonRow && levelNum != null && !dailyLoading ? (
        <div className="mb-0 hidden border-t border-white/40 pt-0 sm:mb-5 sm:block sm:pt-4">
          <span className="inline-flex items-center rounded-full border border-indigo-200/90 bg-indigo-50/95 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-indigo-900/90 ring-1 ring-indigo-100/80 sm:text-[11px]">
            {t('dashboard.student.enthusiasmProgressLevel', { level: levelNum })}
          </span>
        </div>
      ) : null}

      {/* Points du jour — مدمج قليلاً في الارتفاع */}
      <div
        className={`mb-3 flex min-w-0 items-baseline justify-between gap-2 border-t border-white/35 pt-2 sm:mb-4 sm:gap-2.5 sm:pt-2.5 ${
          isRTL ? 'flex-row-reverse' : ''
        }`}
      >
        <span className="min-w-0 text-xs font-bold leading-tight text-slate-800 sm:text-sm">
          {t('dashboard.student.enthusiasmDailyTotalLabel')}
        </span>
        {dailyLoading ? (
          <span className="inline-flex h-7 w-14 animate-pulse rounded-md bg-white/50 sm:h-8 sm:w-16" aria-hidden />
        ) : (
          <span className="flex shrink-0 items-center gap-1 text-end">
            <Flame className="h-4 w-4 shrink-0 text-orange-500 sm:h-[1.125rem] sm:w-[1.125rem]" strokeWidth={2.25} aria-hidden />
            <span className="text-xl font-black tabular-nums leading-none text-slate-900 sm:text-2xl">
              {formatTotal(dailyTotalNum)}
            </span>
          </span>
        )}
      </div>

      <div className="rounded-2xl border-[2px] border-indigo-200/70 bg-gradient-to-b from-indigo-50/90 via-white to-violet-50/30 p-3 shadow-inner shadow-indigo-100/50 ring-1 ring-white/80 sm:p-4">
        <p
          className="mb-2.5 min-w-0 text-[10px] font-semibold leading-snug text-slate-700 sm:mb-3 sm:text-[11px]"
          title={encouragementOneLine}
        >
          <span className="line-clamp-1 block">{encouragementOneLine}</span>
        </p>

        {dailyLoading ? (
          <DailyPathProgressSkeleton />
        ) : (
          <>
            <EnthusiasmPathProgressBar
              breakdown={dailyData?.breakdown}
              dailyData={dailyData}
              compact
            />
            <p className="mt-1.5 border-t border-indigo-100/80 pt-1.5 text-center text-[10px] font-medium tabular-nums text-slate-500 sm:text-[11px]">
              {caption}
            </p>

            {spotlightLoading ? (
              <div className="mt-3 space-y-2" aria-hidden>
                <div className="h-9 w-full animate-pulse rounded-xl bg-slate-100" />
                <div className="h-9 w-full animate-pulse rounded-xl bg-slate-100" />
                <div className="h-9 w-full animate-pulse rounded-xl bg-slate-100" />
              </div>
            ) : showSpotlightRanks ? (
              <div className="mt-3 space-y-2 border-t border-indigo-100/70 pt-3">
                <RankWithMedal place={1} name={n1} compact points={l1?.total ?? l1?.points} />
                <RankWithMedal place={2} name={n2} compact points={l2?.total ?? l2?.points} />
                {ci && nc ? (
                  <RankWithMedal place={3} name={nc} compact points={ci?.total ?? ci?.points} />
                ) : null}
                {warnText ? (
                  <div className="flex gap-1.5 rounded-xl border-[2px] border-rose-200 bg-rose-50/90 px-2 py-1.5 text-rose-950 sm:gap-2 sm:px-2.5 sm:py-2">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-600 sm:h-4 sm:w-4" aria-hidden />
                    <p className="min-w-0 flex-1 text-[10px] font-semibold leading-snug sm:text-[11px] sm:leading-snug line-clamp-2 break-words">
                      {warnText}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

export default StudentSeasonEnthusiasmBar;
